import { Card, Col, Row, Table, Typography, Spin, Alert } from "antd";
import { Link } from "react-router-dom";

const { Text } = Typography;

const STAGE_LABELS = {
  workspace_created: "Workspace created",
  ga4_property_selected: "GA4 property selected",
  replay_connected: "Replay connected",
  onboarding_complete: "Onboarding complete",
  paid_workspace: "Paid",
};

const TONE_COLOR = {
  up: "#389e0d",
  down: "#cf1322",
  neutral: "#8c8c8c",
};

function formatDelta(pct) {
  if (typeof pct !== "number") return "—";
  const sign = pct > 0 ? "+" : "";
  return `${sign}${pct}% vs prior`;
}

function deltaTone(pct) {
  if (typeof pct !== "number" || pct === 0) return "neutral";
  return pct < 0 ? "down" : "up";
}

function formatMoney(amount, currency) {
  if (typeof amount !== "number" || !currency) return "—";
  return `${currency} ${amount.toFixed(2)}`;
}

function formatQuotaLimit(limit) {
  if (limit == null) return "Unlimited";
  return Number(limit).toLocaleString();
}

const metricCardStyle = {
  boxShadow: "none",
  borderRadius: 8,
};

function formatStatValue(value) {
  if (typeof value === "number") return value.toLocaleString();
  if (value == null || value === "") return "—";
  return String(value);
}

function StatCard({ title, value, to, note, tone = "neutral", children }) {
  return (
    <Col xs={12} sm={12} lg={8} xl={4}>
      <Card size="small" style={metricCardStyle} bodyStyle={{ padding: "8px 12px" }}>
        <Text type="secondary" style={{ display: "block", fontSize: 12, lineHeight: "16px" }}>
          {title}
        </Text>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8 }}>
          <span style={{ fontSize: 18, fontWeight: 600, lineHeight: "24px" }}>{formatStatValue(value)}</span>
          {to ? <Link to={to} style={{ fontSize: 12, lineHeight: "16px" }}>View</Link> : null}
        </div>
        {note ? (
          <Text style={{ display: "block", fontSize: 11, lineHeight: "16px", color: TONE_COLOR[tone] || TONE_COLOR.neutral }}>
            {note}
          </Text>
        ) : null}
        {children ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 2, marginTop: 4, fontSize: 12, lineHeight: "16px" }}>
            {children}
          </div>
        ) : null}
      </Card>
    </Col>
  );
}

function QuotaLine({ label, value }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 8, lineHeight: "20px" }}>
      <Text type="secondary" style={{ fontSize: 12 }}>{label}</Text>
      <Text style={{ fontSize: 12 }}>{value}</Text>
    </div>
  );
}

function QuotaBlock({ title, quota }) {
  if (!quota) return null;
  const utilization =
    typeof quota.utilization === "number" ? `${(quota.utilization * 100).toFixed(1)}%` : "—";
  return (
    <Card size="small" style={metricCardStyle} bodyStyle={{ padding: "10px 12px" }}>
      <Text strong style={{ display: "block", marginBottom: 6 }}>{title}</Text>
      <Row gutter={16}>
        <Col span={12}>
          <QuotaLine label="Used this period" value={formatStatValue(quota.used ?? 0)} />
          <QuotaLine label="Finite plan limit" value={formatStatValue(quota.limit ?? 0)} />
          <QuotaLine label="Utilization" value={utilization} />
        </Col>
        <Col span={12}>
          <QuotaLine label="Unlimited" value={formatStatValue(quota.unlimited_workspaces ?? 0)} />
          <QuotaLine label="At 80–99%" value={formatStatValue(quota.at_risk_workspaces ?? 0)} />
          <QuotaLine label="At limit" value={formatStatValue(quota.exhausted_workspaces ?? 0)} />
        </Col>
      </Row>
      {quota.top_workspaces?.length ? (
        <Table
          style={{ marginTop: 8 }}
          size="small"
          pagination={false}
          dataSource={quota.top_workspaces.map((row) => ({ ...row, key: row.workspace_id }))}
          columns={[
            {
              title: "Workspace",
              dataIndex: "workspace_name",
              render: (name, row) =>
                row.workspace_id ? (
                  <Link to={`/workspaces/${row.workspace_id}`}>{name || row.workspace_id}</Link>
                ) : (
                  name || "—"
                ),
            },
            { title: "Plan", dataIndex: "plan_key", render: (v) => v || "—" },
            { title: "Used", dataIndex: "used" },
            {
              title: "Limit",
              dataIndex: "limit",
              render: (v) => formatQuotaLimit(v),
            },
          ]}
        />
      ) : null}
    </Card>
  );
}

export function WorkspaceStatsSection({ stats, funnel, loading, error, part = "snapshot", dashboardData }) {
  if (loading) {
    if (part === "detail") return null;
    return <Spin tip="Loading workspace stats..." />;
  }
  if (error) {
    if (part === "detail") return null;
    return <Alert type="warning" message={error} showIcon />;
  }
  if (!stats) {
    return null;
  }

  const subscription = stats.subscription || {};
  const newPayingDelta = formatDelta(subscription.new_paying_delta_pct);
  const newPayingTone = deltaTone(subscription.new_paying_delta_pct);
  const funnelColumns = [
    {
      title: "Stage",
      dataIndex: "stage",
      key: "stage",
      render: (stage) => STAGE_LABELS[stage] || stage,
    },
    { title: "Workspaces", dataIndex: "count", key: "count" },
    {
      title: "From previous stage",
      dataIndex: "conversion_from_previous_pct",
      key: "conversion_from_previous_pct",
      render: (v) => (typeof v === "number" ? `${v}%` : "—"),
    },
  ];

  const activeDelta = dashboardData?.active_users_7d_delta_pct;
  const activeDeltaTone = deltaTone(activeDelta);
  const activeDeltaLabel = typeof activeDelta !== "number"
    ? "—"
    : `${activeDelta > 0 ? "+" : ""}${activeDelta}% vs prior 7 days`;

  if (part === "detail") {
    const showPlanMix = Boolean(subscription.plan_mix?.length);
    const showFunnel = Boolean(funnel?.stages?.length);
    return (
      <>
        <Row gutter={[12, 12]} style={{ marginTop: 12 }}>
          <Col xs={24} lg={12}>
            <QuotaBlock title="AI quota" quota={stats.ai_quota} />
          </Col>
          <Col xs={24} lg={12}>
            <QuotaBlock title="Smart AI Summary quota" quota={stats.polars_quota} />
          </Col>
        </Row>

        {showPlanMix || showFunnel ? (
          <Row gutter={[12, 12]} style={{ marginTop: 12 }}>
            {showPlanMix ? (
              <Col xs={24} lg={showFunnel ? 10 : 24}>
                <Card size="small" style={metricCardStyle} bodyStyle={{ padding: "10px 12px" }}>
                  <Text strong style={{ display: "block", marginBottom: 6 }}>Plan mix</Text>
                  <Table
                    size="small"
                    pagination={false}
                    dataSource={subscription.plan_mix.map((plan) => ({ ...plan, key: plan.plan_key }))}
                    columns={[
                      { title: "Plan", dataIndex: "plan_key" },
                      { title: "Workspaces", dataIndex: "workspaces" },
                      {
                        title: "",
                        key: "view",
                        width: 56,
                        render: (_, plan) => (
                          <Link to={`/workspace_subscriptions?status=active&plan_key=${encodeURIComponent(plan.plan_key)}`}>
                            View
                          </Link>
                        ),
                      },
                    ]}
                  />
                </Card>
              </Col>
            ) : null}
            {showFunnel ? (
              <Col xs={24} lg={showPlanMix ? 14 : 24}>
                <Card size="small" style={metricCardStyle} bodyStyle={{ padding: "10px 12px" }}>
                  <Text strong style={{ display: "block", marginBottom: 6 }}>Activation funnel</Text>
                  <Table
                    dataSource={funnel.stages.map((stage, i) => ({ ...stage, key: i }))}
                    columns={funnelColumns}
                    pagination={false}
                    size="small"
                  />
                  {funnel.invites ? (
                    <Text type="secondary" style={{ display: "block", marginTop: 8, fontSize: 12 }}>
                      Invites accepted {funnel.invites.accepted} of {funnel.invites.sent}
                      {typeof funnel.invites.acceptance_pct === "number"
                        ? ` (${funnel.invites.acceptance_pct}%)`
                        : ""}
                    </Text>
                  ) : null}
                </Card>
              </Col>
            ) : null}
          </Row>
        ) : null}
      </>
    );
  }

  const mrrRows = subscription.mrr_by_currency || [];
  const primaryMrr = mrrRows[0];

  return (
    <Row gutter={[12, 12]}>
      <StatCard title="Total users" value={dashboardData?.total_users ?? 0} note="Customers, excluding admins" />
      <StatCard
        title="Active users, 7 days"
        value={dashboardData?.active_users_last_7_days ?? 0}
        note={activeDeltaLabel}
        tone={activeDeltaTone}
      />
      <StatCard title="Workspaces" value={stats.workspaces_total} to="/workspaces">
        <Link to="/workspaces?lifecycle_status=active">Active {formatStatValue(stats.workspaces_active)}</Link>
        <Link to="/workspaces?lifecycle_status=suspended">Suspended {formatStatValue(stats.workspaces_suspended)}</Link>
        {stats.workspaces_other_lifecycle > 0 ? (
          <Text type="secondary" style={{ fontSize: 12 }}>Other {formatStatValue(stats.workspaces_other_lifecycle)}</Text>
        ) : null}
        <Text type="secondary" style={{ fontSize: 12 }}>
          Over limit {formatStatValue(stats.blocked_by_plan_workspaces)} · Properties {formatStatValue(stats.blocked_by_plan_properties)} · Unhealthy GA4 {formatStatValue(stats.integration_unhealthy_connections)}
        </Text>
      </StatCard>
      <StatCard
        title="Subscriptions"
        value={subscription.active_subscriptions}
        to="/workspace_subscriptions?status=active"
      >
        <Link to="/workspace_subscriptions?status=active&paid=true">Paying {formatStatValue(subscription.paying_workspaces)}</Link>
        <Link to="/workspace_subscriptions?status=active&paid=false">Free {formatStatValue(subscription.free_workspaces)}</Link>
        <Text type="secondary" style={{ fontSize: 12 }}>
          No subscription {formatStatValue(subscription.workspaces_without_subscription)}
        </Text>
      </StatCard>
      <StatCard
        title={primaryMrr ? `MRR ${primaryMrr.currency}` : "MRR"}
        value={primaryMrr ? formatMoney(primaryMrr.mrr, primaryMrr.currency) : "—"}
        note={primaryMrr ? `ARR ${formatMoney(primaryMrr.arr, primaryMrr.currency)}` : "No paying revenue"}
      >
        {mrrRows.slice(1).map((row) => (
          <Text key={row.currency} type="secondary" style={{ fontSize: 12 }}>
            {row.currency} {formatMoney(row.mrr, row.currency)} · ARR {formatMoney(row.arr, row.currency)}
          </Text>
        ))}
        {subscription.mrr_unknown_interval_workspaces > 0 ? (
          <Text type="secondary" style={{ fontSize: 12 }}>
            Excluded, nonstandard interval {formatStatValue(subscription.mrr_unknown_interval_workspaces)}
          </Text>
        ) : null}
      </StatCard>
      <StatCard
        title="New paying, 30d"
        value={subscription.new_paying_30d}
        note={newPayingDelta}
        tone={newPayingTone}
      >
        <Link to="/workspace_subscriptions?status=canceled&unreplaced_days=30">
          Canceled {formatStatValue(subscription.canceled_workspaces_30d)}, not replaced
        </Link>
      </StatCard>
    </Row>
  );
}
